// ============================================================
// BODY TYPE CONFIGURATION - Polymorphic body type system.
// Defines location tables, hit resolution, cover mappings, and
// default armour for each body type (humanoid/vehicle/amorphous/horde).
// ============================================================
import { rollDie, rollD100 } from './dice';
import type { HitLocation, BodyTypeId } from '../types';

// ── Re-export BodyTypeId for convenience ──────────────────────
export type { BodyTypeId } from '../types';

// ── Types ─────────────────────────────────────────────────────

export const BODY_TYPE_IDS: BodyTypeId[] = ['humanoid', 'vehicle', 'amorphous', 'horde', 'object'];

export const BODY_TYPE_LABELS: Record<BodyTypeId, string> = {
  humanoid: 'Humanoid',
  vehicle: 'Vehicle',
  amorphous: 'Amorphous',
  horde: 'Horde',
  object: 'Object',
};

export interface BodyConfig {
  id: BodyTypeId;
  label: string;
  /** Hit locations for this body type. */
  locations: HitLocation[];
  /** Die size for hit location roll. d100 for humanoid, d10 for vehicle/amorphous. */
  hitDie: 10 | 100;
  /** Whether hit location is determined by digit-reversal of the skill roll (humanoid only). */
  usesDigitReversal: boolean;
  /** Default armour values keyed by location ID (humanoid/amorphous) or facing (vehicle). */
  defaultArmour: Record<string, number>;
  /** Cover level → array of protected location IDs. */
  coverMappings: Record<string, string[]>;
  /** Human-readable cover level labels. */
  coverLabels: Record<string, string>;
  /** Crit table location normalization (e.g. rightArm→arm, leftLeg→leg). */
  critLocationNormalization: Record<string, string>;
  /** Whether the user can choose a specific hit location (Called Shot). */
  supportsCalledShot: boolean;
  /** Whether conditions are enriched with location suffixes (Cr:Head, De:Left Arm). */
  supportsConditionLocations: boolean;
  /** Armour lookup mode: per-location (humanoid/amorphous) or facing-based (vehicle). */
  armourMode: 'per-location' | 'facing';
}

// ── Humanoid Configuration ────────────────────────────────────

const HUMANOID_LOCATIONS: HitLocation[] = [
  { id: 'head', name: 'Head', min: 1, max: 10 },
  { id: 'rightArm', name: 'Right Arm', min: 11, max: 20 },
  { id: 'leftArm', name: 'Left Arm', min: 21, max: 30 },
  { id: 'body', name: 'Body', min: 31, max: 70 },
  { id: 'rightLeg', name: 'Right Leg', min: 71, max: 85 },
  { id: 'leftLeg', name: 'Left Leg', min: 86, max: 100 },
];

const HUMANOID_CONFIG: BodyConfig = {
  id: 'humanoid',
  label: 'Humanoid',
  locations: HUMANOID_LOCATIONS,
  hitDie: 100,
  usesDigitReversal: true,
  defaultArmour: { head: 0, rightArm: 0, leftArm: 0, body: 0, rightLeg: 0, leftLeg: 0 },
  coverMappings: {
    none: [],
    low: ['rightLeg', 'leftLeg'],
    half: ['body', 'rightLeg', 'leftLeg'],
    threeQuarter: ['rightArm', 'leftArm', 'body', 'rightLeg', 'leftLeg'],
    full: ['head', 'rightArm', 'leftArm', 'body', 'rightLeg', 'leftLeg'],
  },
  coverLabels: {
    none: 'None',
    low: 'Low (Legs)',
    half: 'Half (Body+Legs)',
    threeQuarter: '\u00BE (All but Head)',
    full: 'Full (All)',
  },
  critLocationNormalization: {
    head: 'head',
    rightArm: 'arm',
    leftArm: 'arm',
    body: 'body',
    rightLeg: 'leg',
    leftLeg: 'leg',
  },
  supportsCalledShot: true,
  supportsConditionLocations: true,
  armourMode: 'per-location',
};

// ── Vehicle Configuration ─────────────────────────────────────
// Standard 40K RPG: Hull, Turret, Motive, Rear - d10 roll.
// Armour is facing-based (front/side/rear), NOT per-location.
// Hit location determines crit table only.

const VEHICLE_LOCATIONS: HitLocation[] = [
  { id: 'hull', name: 'Hull', min: 1, max: 4 },
  { id: 'turret', name: 'Turret', min: 5, max: 6 },
  { id: 'motive', name: 'Motive', min: 7, max: 8 },
  { id: 'rear', name: 'Rear', min: 9, max: 10 },
];

const VEHICLE_CONFIG: BodyConfig = {
  id: 'vehicle',
  label: 'Vehicle',
  locations: VEHICLE_LOCATIONS,
  hitDie: 10,
  usesDigitReversal: false,
  defaultArmour: { front: 30, side: 25, rear: 15 },
  coverMappings: {
    none: [],
    low: ['motive'],
    half: ['hull', 'motive'],
    threeQuarter: ['hull', 'motive', 'rear'],
    full: ['hull', 'turret', 'motive', 'rear'],
  },
  coverLabels: {
    none: 'None',
    low: 'Low (Motive)',
    half: 'Half (Hull+Motive)',
    threeQuarter: '\u00BE (All but Turret)',
    full: 'Full (All)',
  },
  critLocationNormalization: {
    hull: 'hull',
    turret: 'turret',
    motive: 'motive',
    rear: 'hull', // Rear crits use Hull crit table
  },
  supportsCalledShot: true,
  supportsConditionLocations: true,
  armourMode: 'facing',
};

// ── Amorphous Configuration ───────────────────────────────────
// Single "Mass" location. No digit reversal, no called shot.
// All hits go to the same location.

const AMORPHOUS_LOCATIONS: HitLocation[] = [{ id: 'mass', name: 'Mass', min: 1, max: 10 }];

const AMORPHOUS_CONFIG: BodyConfig = {
  id: 'amorphous',
  label: 'Amorphous',
  locations: AMORPHOUS_LOCATIONS,
  hitDie: 10,
  usesDigitReversal: false,
  defaultArmour: { mass: 0 },
  coverMappings: {
    none: [],
    low: [],
    half: ['mass'],
    threeQuarter: ['mass'],
    full: ['mass'],
  },
  coverLabels: {
    none: 'None',
    low: 'None (Amorphous)',
    half: 'Partial',
    threeQuarter: 'Substantial',
    full: 'Full',
  },
  critLocationNormalization: {
    mass: 'mass', // Uses dedicated amorphous crit table
  },
  supportsCalledShot: false,
  supportsConditionLocations: false,
  armourMode: 'per-location',
};

// ── Object Configuration (2026-10-26) ─────────────────────────
// An inanimate thing: one location, the Structure. No digit reversal, no called shot, no condition locations; it
// is destroyed at once past 0 Wounds (hitOutcome), so its crit table is never read.

const OBJECT_CONFIG: BodyConfig = {
  id: 'object',
  label: 'Object',
  locations: [{ id: 'structure', name: 'Structure', min: 1, max: 10 }],
  hitDie: 10,
  usesDigitReversal: false,
  defaultArmour: { structure: 0 },
  coverMappings: {
    none: [],
    low: [],
    half: ['structure'],
    threeQuarter: ['structure'],
    full: ['structure'],
  },
  coverLabels: {
    none: 'None',
    low: 'None (Object)',
    half: 'Partial',
    threeQuarter: 'Substantial',
    full: 'Full',
  },
  critLocationNormalization: { structure: 'structure' },
  supportsCalledShot: false,
  supportsConditionLocations: false,
  armourMode: 'per-location',
};

// ── Horde Configuration ───────────────────────────────────────
// Hordes reuse humanoid locations structurally but the engine
// skips per-hit location tracking (magnitude system).
// Body map renders the crowd cluster, not individual anatomy.

const HORDE_CONFIG: BodyConfig = {
  ...HUMANOID_CONFIG,
  id: 'horde',
  label: 'Horde',
  supportsCalledShot: false,
  supportsConditionLocations: false,
};

// ── Registry ──────────────────────────────────────────────────

export const BODY_CONFIGS: Record<BodyTypeId, BodyConfig> = {
  humanoid: HUMANOID_CONFIG,
  vehicle: VEHICLE_CONFIG,
  amorphous: AMORPHOUS_CONFIG,
  horde: HORDE_CONFIG,
  object: OBJECT_CONFIG,
};

// ── Helpers ───────────────────────────────────────────────────

/** Get the body config for a given body type. Falls back to humanoid. */
export function getBodyConfig(bodyType: BodyTypeId | undefined): BodyConfig {
  return BODY_CONFIGS[bodyType || 'humanoid'] || BODY_CONFIGS.humanoid;
}

/**
 * Resolve a hit location from a roll for a given body config.
 * For humanoid: applies digit reversal to the skill roll.
 * For vehicle/amorphous: the d10 table is read with the roll's ones digit.
 */
export function getHitLocationForBody(roll: number, config: BodyConfig): HitLocation {
  const val = locationValue(roll, config);
  // Fallback: first location (body for humanoid, hull for vehicle, mass for amorphous)
  return config.locations.find((loc) => val >= loc.min && val <= loc.max) ?? config.locations[0];
}

/**
 * The value the location table is read with: the reversed digits for a
 * humanoid, else the d100 attack roll folded onto the body's die.
 */
function locationValue(roll: number, config: BodyConfig): number {
  // A d10 body reads the attack roll's ONES DIGIT (0 reads as 10). The ones
  // digit of a uniform d100 is a uniform d10, matching the forecast's own
  // d10, and no extra die is drawn - a second draw would shift every seeded
  // E2E roll (CLAUDE.md gotcha 5). It used to CLAMP the roll to 1..10, which
  // sent every roll of 9 or more to the last row (Rear for a vehicle).
  if (!config.usesDigitReversal) return ((roll - 1) % config.hitDie) + 1;
  // Humanoid digit-reversal mechanic
  const s = roll.toString().padStart(2, '0');
  const reversed = parseInt(s[1] + s[0]);
  return reversed === 0 ? 100 : reversed;
}

/**
 * Roll a random hit location for a given body config.
 * Humanoid: d100. Vehicle/amorphous: d10.
 */
export function getRandomHitLocationForBody(config: BodyConfig): { loc: HitLocation; roll: number } {
  const roll = config.hitDie === 100 ? rollD100() : rollDie(config.hitDie);

  for (const loc of config.locations) {
    if (roll >= loc.min && roll <= loc.max) return { loc, roll };
  }
  return { loc: config.locations[0], roll };
}

/** Check if a location is covered at a given cover level for a body config. */
export function isLocCoveredForBody(level: string, locId: string, config: BodyConfig): boolean {
  return (config.coverMappings[level] || []).includes(locId);
}

/** Get a location name from its ID for a given body config. */
export function getLocNameForBody(locId: string, config: BodyConfig): string {
  const loc = config.locations.find((l) => l.id === locId);
  return loc ? loc.name : locId;
}

/** Get the default armour record for a body type. */
export function defaultArmourForBody(bodyType: BodyTypeId | undefined): Record<string, number> {
  const config = getBodyConfig(bodyType);
  return { ...config.defaultArmour };
}

/**
 * Resolve the effective AP for a hit on a given location.
 * - Per-location bodies (humanoid, amorphous): AP = armour[location.id]
 * - Facing bodies (vehicle): AP = armour[vehicleFacing]
 */
export function resolveArmourAP(
  armour: Record<string, number>,
  locationId: string,
  config: BodyConfig,
  vehicleFacing?: string | null,
): number {
  return armour[armourKeyFor(locationId, config, vehicleFacing)] || 0;
}

/**
 * The armour record key a hit on `locationId` reads - and wears down:
 * the location itself, or for a facing body (vehicle) the facing it
 * presents (front when unset). Corrosive and Sunder strip this same key,
 * so they always wear the armour that soaked the hit.
 */
export function armourKeyFor(locationId: string, config: BodyConfig, vehicleFacing?: string | null): string {
  return config.armourMode === 'facing' ? vehicleFacing || 'front' : locationId;
}
